import API from "./API.js";
import { TokenExpiredError } from "../errors/TokenExpiredError.js";

class AuthAPI {
	constructor(baseUrl, config) {
		this._notaryAPI = new API(window.Notary.Config.ApiUrlBase + `/API`, config);
	}

	async GET(route, body, headers, params) {
		return await this._fetchWithRetry(headers, async (headers2) => await this._notaryAPI.GET(route, body, headers2, params));
	}

	async GETblob(route, headers) {
		return await this._fetchWithRetry(headers, async (headers) => await this._notaryAPI.GETblob(route, headers));
	}

	async GETfile(route, headers) {
		return await this._fetchWithRetry(headers, async (headers) => await this._notaryAPI.GETfile(route, headers));
		
	}

	async POST(route, body, headers, keepalive = false) {
		return await this._fetchWithRetry(headers, async (headers) => await this._notaryAPI.POST(route, body, headers, keepalive));
	}

	// Posts a js obj like a form to an API endpoint, converting the js obj to a FormData recursively
	async POSTForm(route, data, headers) {
		return await this._fetchWithRetry(headers, async (headers) => await this._notaryAPI.POSTForm(route, data, headers));
	}

	// Posts FormData to an API endpoint. To convert a Js Obj to FormData, use PostForm(...)
	async POSTFormRaw(route, formData, headers) {
		return await this._fetchWithRetry(headers, async (headers) => await this._notaryAPI.POSTFormRaw(route, formData, headers));
	}

	async POSTFile(route, data, headers) {
		headers = headers || new window.Headers();
		headers.append(`Content-Type`, `multipart/form-data`);
		return await this._fetchWithRetry(headers, async (headers) => {
			const formData = new FormData();
			for (const key in data) {
				formData.append(key, data[key]);
			}
			return await this._notaryAPI.POST(route, formData, headers);	
		});
	}

	async PATCH(route, body, headers) {
		return await this._fetchWithRetry(headers, async (headers) => await this._notaryAPI.PATCH(route, body, headers));
	}

	async DELETE(route, body, headers) {
		return await this._fetchWithRetry(headers, async (headers) => await this._notaryAPI.DELETE(route, body, headers));
	}

	async PUT(route, body, headers) {
		return await this._fetchWithRetry(headers, async (headers) => await this._notaryAPI.PUT(route, body, headers));
	}

	async signalRAsync(route) {
		const token = await this._token();
		return {
			url: new URL(window.Notary.Config.ApiUrlBase + `/API/signalr` + route),
			token: token
		};
	}

	// Private
	_notaryAPI;

	async _fetchWithRetry(headers, fetch) {
		headers = await this._commonHeaders(headers);
		try {
			return await fetch(headers);
		} catch (e) {
			if(e instanceof TokenExpiredError) {				
				this._clearToken();				
				headers = await this._commonHeaders(headers);				
				return await fetch(headers);
			} else {
				throw e;
			}
		}
	}

	async _commonHeaders(headers) {
		const token = await this._token();

		headers = headers || new window.Headers();
		headers.set(`Authorization`, `bearer ${token}`);

		return headers;
	}

	_clearToken() {
		sessionStorage.removeItem(`notarizationApiToken`);
	}

	async _token() {		
		const token = sessionStorage[`notarizationApiToken`];
		const expiry = Date.parse(sessionStorage[`notarizationApiTokenExpiry`]);
		//Check to see we have a valid token
		if (token && expiry > Date.now()) {
			//If we have a token make sure the same SID was used to create it as what currently set in the window object
			const createdSID = sessionStorage[`notarizationApiTokenCreatedSID`];
			if (createdSID && createdSID === window.SID) {
				//If we got here the token should be good to use the existing token
				return token;
			}
		}

		return await this._loadToken();
	}

	async _loadToken() {
		const data = await this._notaryAPI
			.POST(`/authentication/token`, {
				ApiKey: window.Notary.Config.ApiKey,
				SecureId: window.SID
			});

		if (data.token) {
			sessionStorage[`notarizationApiToken`] = data.token;
			sessionStorage[`notarizationApiTokenCreatedSID`] = window.SID;
			sessionStorage[`notarizationApiTokenExpiry`] = Date.parse(data.validTo);
		}

		return data.token;
	}

}

export default AuthAPI;
